<template>
  <v-container fluid grid-list-sm>
    <pr-loading v-model="loading" />
    <v-snackbar v-model="msg.show" value="true" right top multi-line :color="msg.type" :timeout="2000" >
      {{ msg.text }}
      <v-btn dark flat @click="msg.show = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <v-layout row wrap align-start>
      <v-flex xs12>
        <h1>{{ $t('Import template') }}</h1>
      </v-flex>
      <v-flex xs12>
        {{ $t('Import.help') }}
      </v-flex>

      <v-flex xs11 sm5 md4>
        <v-combobox
          :items="manifests"
          v-model="manifest"
          :search-input.sync="manifest"
          :label="$t('Shipment')"
          item-value="manifest" item-text="manifest"
          :hint="manifestHint" persistent-hint
          hide-no-data
          @keyup.enter="!manifestSelected ? findManifest() : ''"
          clearable
          max-height="auto"
          dense
          data-menu-props="closeOnContentClick"
          :menu-props="{ closeOnContentClick: true, bottom: true, auto: true }"
        >
          <template v-slot:item="{ item }">
            <v-list-tile :class="[item.manifest === manifest && 'blue lighten-4', 'py-1']">
              <v-list-tile-content @click="selectManifest(item)" style="cursor: pointer">
                <v-list-tile-title v-html="item.manifest"></v-list-tile-title>
                <v-list-tile-sub-title v-html="item.hint" class="body-1"></v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-tooltip bottom>
                  <v-btn slot="activator" icon @click.stop="removeManifest(item)">
                    <v-icon color="primary">delete</v-icon>
                  </v-btn>
                  {{ $t('Remove from list') }}
                </v-tooltip>
              </v-list-tile-action>
            </v-list-tile>
          </template>
        </v-combobox>
      </v-flex>
      <v-flex xs1 pt-2>
        <v-tooltip bottom>
          <v-btn slot="activator" icon class="pa-0" @click="findManifest" :disabled="!!manifestSelected">
            <v-icon>search</v-icon>
          </v-btn>
          {{ $t('Find manifest') }}
        </v-tooltip>
      </v-flex>

      <v-flex xs11 sm5 md4>
        <v-text-field
          v-model="filename" :label="$t('Import filename')"
          readonly persistent-hint
          :hint="$t('Select XLS file template')"
        ></v-text-field>
      </v-flex>
      <v-flex xs1 pt-2>
        <v-tooltip bottom>
          <v-upload-button slot="activator" :fileChangedCallback="fileSelected" title="" icon="attach_file" color="transparent" class="pa-0"></v-upload-button>
          {{ $t('Browse') }}
        </v-tooltip>
      </v-flex>

      <v-flex sm12 md2 class="text-xs-right" pt-2>
        <v-tooltip bottom>
          <v-btn flat slot="activator" @click="onProcess" :disabled="processDisabled" color="primary">
            <v-icon>{{ processButtonIcon }}</v-icon>&nbsp;
            {{ processButtonText }}
          </v-btn>
          {{ processButtonTip }}
        </v-tooltip>
      </v-flex>

    </v-layout>
    <v-layout row wrap align-center>

      <v-flex xs12 class="pt-4">
        <v-alert :value="errors.length > 0" type="error">
          <p v-for="err in errors">{{ err }}</p>
        </v-alert>
        <v-data-table v-if="process" :headers="headers" :items="data" item-key="tableKey" hide-actions class="elevation-1">
          <template slot="headers" slot-scope="props">
            <tr>
              <th v-for="(header,idx) in props.headers"
                :key="idx" v-if="header.show && isColumnVisible(header)"
                :class="['column sortable', '' ]"
              >
                <v-icon small>arrow_upward</v-icon>
                <v-tooltip v-if="header.tooltip" bottom>
                  <span slot="activator">
                    {{ header.text }}
                  </span>
                  <span>
                    {{ header.tooltip }}
                  </span>
                </v-tooltip>
                <span v-else>
                  {{ header.text }}
                </span>
              </th>
            </tr>
          </template>
          <template slot="items" slot-scope="props">
            <tr @click="props.expanded = !props.expanded">
              <td v-for="(header,idx) in headers" :key="idx" v-if="header.show && isColumnVisible(header)" :class="getColumnAlign(idx)" class="line-spacing">
                {{ getObjectValue(props.item, idx) }}
              </td>
            </tr>
          </template>
          <template slot="expand" slot-scope="props">

          <v-container fluid grid-list-md style="background-color: #f6f6f6" class="pa-2">
            <v-layout row wrap>
              <v-flex xs12 sm6 md3>
                {{ $t('Source Partner') }}: <span class="font-weight-bold">{{ props.item.source.partner.name }}</span>
              </v-flex>
              <v-flex xs12 sm6 md3>
                {{ $t('ContactName') }}: <span class="font-weight-bold">{{ props.item.source.contact_person }}</span>
              </v-flex>
              <v-flex xs12 sm6 md3>
                {{ $t('Phone') }}: <span class="font-weight-bold">{{ props.item.source.contact_phone }}</span><span v-if="props.item.source.call_required">({{ $t('Call Required') }})</span>
              </v-flex>
              <v-flex xs12 sm6 md3>
                {{ $t('Email') }}: <span class="font-weight-bold">{{ props.item.source.email }}</span>
              </v-flex>
              <v-flex xs12 sm6 md6 v-if="!props.item.source.direct">
                <v-tooltip bottom>
                  <span slot="activator">{{ $t('Office') }}: <span class="font-weight-bold">{{ props.item.source.station.name }}</span></span>
                  <span>{{ $t('Pickup from Tranpress office') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs12 sm6 md6 v-if="props.item.source.direct">
                <v-tooltip bottom>
                  <span slot="activator">
                    {{ $t('Source Address') }}: <span class="font-weight-bold">{{ displayAddress(props.item.source.address) }}</span>
                  </span>
                  <span>{{ $t('Pickup from client\'s door') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs4 sm2 md1>
                <v-tooltip bottom>
                  <span slot="activator">
                    <v-icon>{{ props.item.source.tail_lift ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                    {{ $t('Tail lift.short') }}
                  </span>
                  <span>{{ $t('Tail lift.explained') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs4 sm2 md1>
                <v-tooltip bottom>
                  <span slot="activator">
                    <v-icon>{{ props.item.source.extended_convey ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                    {{ $t('Extended convey.short') }}
                  </span>
                  <span>{{ $t('Extended convey.explained') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs4 sm2 md1>
                <v-tooltip bottom>
                  <span slot="activator">
                    <v-icon>{{ props.item.source.thc ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                    {{ $t('Handling.short') }}
                  </span>
                  <span>{{ $t('Handling.explained') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs12 :class="props.item.source.instructions ? 'sm6' : 'sm12'" md3 v-if="props.item.source.access">
                <v-tooltip bottom>
                  <span slot="activator">
                    {{ $t('Access') }}: <span class="font-weight-bold">{{ props.item.source.access }}</span>
                  </span>
                  <span>{{ $t('Access.'+ props.item.source.access+'.explained') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs12 :class="props.item.source.access ? 'sm6' : 'sm12'" md12 v-if="props.item.source.instructions">
                {{ $t('Instructions') }}: <span class="font-weight-bold">{{ props.item.source.instructions }}</span>
              </v-flex>
            </v-layout>

            <v-divider class="mx-n2"></v-divider>

            <v-layout row wrap>
              <v-flex xs12 sm6 md3>
                {{ $t('Target Partner') }}: <span class="font-weight-bold">{{ props.item.target.partner.name }}</span>
              </v-flex>
              <v-flex xs12 sm6 md3>
                {{ $t('ContactName') }}: <span class="font-weight-bold">{{ props.item.target.contact_person }}</span>
              </v-flex>
              <v-flex xs12 sm6 md3>
                {{ $t('Phone') }}: <span class="font-weight-bold">{{ props.item.target.contact_phone }}</span><span v-if="props.item.target.call_required">({{ $t('Call Required') }})</span>
              </v-flex>
              <v-flex xs12 sm6 md3>
                {{ $t('Email') }}: <span class="font-weight-bold">{{ props.item.target.email }}</span>
              </v-flex>
              <v-flex xs12 sm6 md6 v-if="!props.item.target.direct">
                <v-tooltip bottom>
                  <span slot="activator">{{ $t('Office') }}: <span class="font-weight-bold">{{ props.item.target.station.name }}</span></span>
                  <span>{{ $t('Pickup from Tranpress office') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs12 sm6 md6 v-if="props.item.target.direct">
                <v-tooltip bottom>
                  <span slot="activator">
                    {{ $t('Target Address') }}: <span class="font-weight-bold">{{ displayAddress(props.item.target.address) }}</span>
                  </span>
                  <span>{{ $t('Pickup from client\'s door') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs4 sm2 md1>
                <v-tooltip bottom>
                  <span slot="activator">
                    <v-icon>{{ props.item.target.tail_lift ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                    {{ $t('Tail lift.short') }}
                  </span>
                  <span>{{ $t('Tail lift.explained') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs4 sm2 md1>
                <v-tooltip bottom>
                  <span slot="activator">
                    <v-icon>{{ props.item.target.extended_convey ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                    {{ $t('Extended convey.short') }}
                  </span>
                  <span>{{ $t('Extended convey.explained') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs4 sm2 md1>
                <v-tooltip bottom>
                  <span slot="activator">
                    <v-icon>{{ props.item.target.thc ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                    {{ $t('Handling.short') }}
                  </span>
                  <span>{{ $t('Handling.explained') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs12 :class="props.item.target.instructions ? 'sm6' : 'sm12'" md3 v-if="props.item.target.access">
                <v-tooltip bottom>
                  <span slot="activator">
                    {{ $t('Access') }}: <span class="font-weight-bold">{{ props.item.target.access }}</span>
                  </span>
                  <span>{{ $t('Access.'+ props.item.target.access+'.explained') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs12 :class="props.item.target.access ? 'sm6' : 'sm12'" md12 v-if="props.item.target.instructions">
                {{ $t('Instructions') }}: <span class="font-weight-bold">{{ props.item.target.instructions }}</span>
              </v-flex>
            </v-layout>

            <v-divider class="mx-n2"></v-divider>

            <v-layout row wrap>
              <v-flex xs12>
                {{ $t('Description') }}: <span class="font-weight-bold">{{ props.item.description }}</span>
              </v-flex>
              <v-flex xs12 class="px-0">
                <v-container fluid grid-list-xs class="pa-0">
                  <v-layout v-for="(item,idx) in props.item.contents" :key="idx" row wrap class="contents-item px-1">
                    <v-flex xs3 sm2 md1>
                      {{ $t('Count.short') }}: <span class="font-weight-bold">{{ item.count }}</span>
                    </v-flex>
                    <v-flex xs9 sm4 md2>
                      {{ $t('Packaging') }}: <span class="font-weight-bold">{{ item.packaging.code }}</span>
                    </v-flex>
                    <v-flex xs6 sm3 md2>
                      {{ $t('Weight') }}: <span class="font-weight-bold">{{ item.weight }}</span> kg
                    </v-flex>
                    <v-flex xs6 sm3 md1>
                      {{ $t('Volume') }}: <span class="font-weight-bold">{{ item.volume }}</span> m³
                    </v-flex>
                    <v-flex xs6 sm4 md2>
                      {{ $t('Parcel_X') }}: <span class="font-weight-bold">{{ item.parcel_x }}</span> m
                    </v-flex>
                    <v-flex xs6 sm4 md2>
                      {{ $t('Parcel_Y') }}: <span class="font-weight-bold">{{ item.parcel_y }}</span> m
                    </v-flex>
                    <v-flex xs9 sm4 md2>
                      {{ $t('Parcel_Z') }}: <span class="font-weight-bold">{{ item.parcel_z }}</span> m
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-flex>
            </v-layout>

            <v-divider class="mx-n2"></v-divider>

            <v-layout row wrap>
              <v-flex xs12>
                {{ $t('Service type') }}: <span class="font-weight-bold">{{ props.item.service.type }}</span>
              </v-flex>
            </v-layout>

            <v-divider class="mx-n2"></v-divider>

            <v-layout row wrap>
              <v-flex xs6 sm6 md3 lg2>
                <v-icon>{{ props.item.cash_on_delivery.cash_on_delivery ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                {{ $t('Cash On Delivery') }}
              </v-flex>
              <v-flex xs6 sm6 md3 lg3 v-if="props.item.cash_on_delivery.cash_on_delivery">
                <v-tooltip bottom>
                  <span slot="activator">
                    {{ $t('COD Payment Type') }}: <span class="font-weight-bold">{{ $t('CodPaymentType.' + props.item.cash_on_delivery.payment_type) }}</span>
                  </span>
                  <span>{{ $t('COD Payment Type tooltip') }}</span>
                </v-tooltip>
              </v-flex>
              <v-flex xs6 sm6 md3 lg4 v-if="props.item.cash_on_delivery.cash_on_delivery">
                {{ $t('Cash On Delivery Amount') }}: <span class="font-weight-bold">{{ displayCOD(props.item.cash_on_delivery.amount) }}</span> 
              </v-flex>
              <v-flex xs6 sm6 md3 lg3 v-if="props.item.cash_on_delivery.cash_on_delivery">
                <v-tooltip bottom>
                  <span slot="activator">
                    <v-icon>{{ props.item.cash_on_delivery.postal ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                    {{ $t('Postal') }}
                  </span>
                  <span>{{ $t('Postal money transfer') }}</span>
                </v-tooltip>
              </v-flex>
            </v-layout>

            <v-divider class="mx-n2"></v-divider>

            <v-layout row wrap>
              <v-flex xs12 sm6 md3>
                <v-icon>{{ props.item.insurance.active ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                {{ $t('Insurance') }}
              </v-flex>
              <v-flex xs6 sm6 md3 v-if="props.item.insurance.active">
                <v-icon>{{ props.item.insurance.fragile ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                {{ $t('Fragile') }}
              </v-flex>
              <v-flex xs6 sm6 md3 v-if="props.item.insurance.active">
                <v-icon>{{ props.item.insurance.used ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                {{ $t('Used') }}
              </v-flex>
              <v-flex xs6 sm6 md3 v-if="props.item.insurance.active">
                {{ $t('Liability Amount') }}: <span class="font-weight-bold">{{ props.item.insurance.liability.amount }}</span> лв
              </v-flex>
            </v-layout>

            <v-divider class="mx-n2"></v-divider>

            <v-layout row wrap>
              <v-flex xs12 sm6 md8 lg9>
                <v-tooltip bottom>
                  <span slot="activator">
                    {{ $t('Packaging Handling') }}: <span class="font-weight-bold">{{ $t('PackagingHandling.' + props.item.packaging_handling) }}</span>
                  </span>
                  <span>{{ $t('Packaging Handling tooltip') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs12 sm6 md4 lg3>
                <v-icon>{{ props.item.packaging_stretching ? 'check_circle_outline' : 'highlight_off' }}</v-icon>
                {{ $t('Stretching') }}
              </v-flex>

              <!-- <v-flex v-for="c in connectedShipments" :key="c" xs12 sm6 md3>
                  <v-checkbox v-model="shipment[c]" :label="$t('ConnectedShipment.' + c)"/>
              </v-flex> -->
            </v-layout>
          </v-container>
<!--
              <div>
                  <code>
                  {{ props.item }}
                  </code>
              </div>
-->
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import PrLoading from '@/components/Loading';
import VUploadButton from "@/components/UploadButton";
import _object from "lodash/object";
import _lang   from "lodash/lang";
import { shipments } from "../resources";
import { addressString, showDualAmount } from "../components/util";
import { mapGetters, mapActions } from "vuex";

export default {
  components : {
    PrLoading,
    VUploadButton
  },

  data() {
    return {
      loading : false,
      msg : {
        show: false,
        text: '',
        type: ''
      },

      manifest : '',
      file : null,
      data : [],
      errors : [],
      headers : [
          { text: this.$t('Source Partner'), value: 'source.partner', display: this.displayPartner, align: 'center', sortable: false, show : true, hidesmall: true },
          { text: this.$t('Source Address'), value: 'source.address', display: this.displayAddress, align: 'left', sortable: false, show : true, hidesmall: true },
          { text: this.$t('Target Partner'), value: 'target.partner', display: this.displayPartner, align: 'center', sortable: false, show : true },
          { text: this.$t('Target Address'), value: 'target.address', display: this.displayAddress, align: 'left', sortable: false, show : true },
          { text: this.$t('Description'),    value: 'description', align: 'left', sortable: false, show : true, hidesmall: true },
          { text: this.$t('Service Type'),   value: 'service.type', type: 'enum', enum : 'ServiceType', align: 'center', sortable: false, show : true, hidesmall: true },
          { text: this.$t('Count'),  value: 'contents', display: this.displayContentsCount,  align: 'right', sortable: false, show : true },
          { text: this.$t('Weight'), value: 'contents', display: this.displayContentsWeight, align: 'center', sortable: false, show : true },
      ],
      manifests : [],
    };
  },

  watch: {
  },

  computed: {
    ...mapGetters(["requestPrefs"]),

    filename() {
      return this.file ? this.file.name : "";
    },

    process() {
      return this.data && this.data.length > 0;
    },

    processButtonText() {
      return this.process ? this.$t("Process") : this.$t("Load");
    },

    processButtonTip() {
      let tip = this.process ? this.$t("Import template tooltip") : this.$t("Load template");
      if (this.processButtonWarningTip)
        tip += ' - ' + this.processButtonWarningTip;
      return tip;
    },

    processButtonWarningTip() {
      return this.processDisabled &&
        (!this.manifestSelected && !this.file && this.$t("Please select existing template manifest and a file to upload")) ||
        (!this.manifestSelected &&  this.file && this.$t("Please select existing template manifest - already loaded and present in the list, or a found one")) ||
        ( this.manifestSelected && !this.file && this.$t("Please select file to upload first")) ||
        "";
    },

    processButtonIcon() {
      return this.process ? "settings_applications" : "youtube_searched_for";
    },

    processDisabled() {
      return this.process ? false : !(this.manifestSelected && this.file);
    },

    isManifest() {
      return !!this.manifest && (this.manifest + "").trim().length > 0;
    },

    manifestSelected() {
      return this.manifests.find(elem => elem.manifest === this.manifest);
    },

    manifestHint() {
      if (this.manifestSelected)
        return this.manifestSelected.hint;
      return this.$t("Select existing manifest to be used for source template");
    },
  },

  methods: {
    ...mapActions(["updateRequestPrefs"]),

    isColumnVisible(header) {
      return !(header.hidesmall && this.$vuetify.breakpoint.smAndDown)
    },

    displayAddress(a) {
      let t = {
        block: this.$t("block"),
        floor: this.$t("floor"),
        entrance: this.$t("entrance"),
        apartment: this.$t("apartment")
      };

      let d = addressString(a, t);
      return d.address;
    },

    displayPartner(p) {
      return (!p) ? "" : p.name;
    },

    displayContentsCount(e) {
      if(!e || !e.length)
        return 0;

      return e
        .filter(elem => elem && elem.count)
        .map(elem => elem.count)
        .reduce((a, b) => a + b, 0);

      let s = 0;
      for (let x=0; x<e.length; x++)
        s+= e[x] ? e[x].count || 0 : 0;
      return s;
    },

    displayContentsWeight(e) {
      if(!e || !e.length)
        return 0;

      let s = 0;
      for (let x=0; x<e.length; x++)
        s+= e[x] ? e[x].weight || 0 : 0;
      return s;
    },

    getColumnAlign(idx) {
      if (this.headers[idx].align)
        return "text-xs-" + this.headers[idx].align;
      return "text-xs-left";
    },

    getObjectValue(obj, idx) {
      let v = _object.get(obj, this.headers[idx].value);
      let d = this.headers[idx].display;
      let t = this.headers[idx].type;

      if (typeof d === 'function')
        return d(v);

      if (t === 'date')
        return v ? new Date(v).toLocaleDateString() : '';
      if (t === 'datetime')
        return v ? new Date(v).toLocaleString() : '';

      if (t === 'enum' && v) {
        let e = this.headers[idx].enum;
        if (!e) e = "Enum";
        return this.$t(e + '.' + v);
      }

      if (typeof v === 'boolean')
        return this.$t('Boolean.' + v);

      if (typeof v === 'number')
        return v.toLocaleString();

      return v;
    },

    cleanup() {
      this.data = [];
      this.errors = [];
    },

    fileSelected(e) {
      this.file = (!!e) ? e : null;
      this.cleanup();
    },

    onProcess() {
      if (this.process)
        this.doProcess();
      else
        this.dataLoad();
    },

    doProcess() {
      console.log("Do process " + this.data.length + " shipments to import");
      if (this.loading)
        return;
      this.loading = true;
      let MAX_THREADS = 2;
      for (let i = 0; i < MAX_THREADS; i++) {
        console.log("Starting Thread#" + i);
        this.doProcessItem();
      }
    },

    doProcessItem() {
      if (this.data.length === 0) {
        this.loading = false;
        return;
      }
      let d = this.data.shift();
      console.log("Processing item #" + d.tableKey + " left " + this.data.length);
      delete d.tableKey;
      delete d.progress;
      if (d.source && d.source.partner && !d.source.partner.code && d.source.partner.name)
        delete d.source.partner.code
      if (d.target && d.target.partner && !d.target.partner.code && d.target.partner.name)
        delete d.target.partner.code;

      if (d.source && d.source.address && d.source.address.settlement) {
        delete d.source.address.settlement.client_message;
        delete d.source.address.settlement.delivery_days_cargo;
        delete d.source.address.settlement.delivery_days_courier;
      }
      if (d.target && d.target.address && d.target.address.settlement) {
        delete d.target.address.settlement.client_message;
        delete d.target.address.settlement.delivery_days_cargo;
        delete d.target.address.settlement.delivery_days_courier;
      }

      shipments.create(d).then((response) => {
        console.log("Item processed successfully #" + (response.body.id));
      }).catch((error) => {
        console.log("Error processing item", error);
        this.doProcessErrors(error.body);
      }).finally(() => {
        if (this.data.length > 0)
          this.doProcessItem();
        else
          this.loading = false;
      });
    },

    doProcessErrors(body) {
      console.log("doProcessErrors");
      let e = body.errors;
      if (e) {
        for (let x = 0; x < e.length; x++) {
          let//
          msg = (e[x].path && e[x].text) ? e[x].path + ": " + e[x].text : e[x];
          this.errors.push(msg);
        }
      }
    },

    dataLoad() {
      if (!this.file || !this.manifestSelected) {
        let msgText = "";
        if (!this.file)
          msgText += this.$t("Please select file to upload first");
        if (!this.manifestSelected)
          msgText += (msgText.length > 0 ? " " : "") + this.$t("Please select existing template manifest to import with");
        this.showMsg(msgText, "warning");
        return;
      }
      this.loadShipment(this.manifestSelected.id);
    },

    loadShipment(id) {
      if (this.loading)
        return;
      this.loading = true;
      shipments.load({ id }).then(r => {
        let shipment = r.body;
        this.importFile(shipment);
      }).catch(e => {
        console.log("Error loading shipment", e);
        this.doProcessErrors(e.body);
        this.loading = false;
      }).finally(() => {
        console.log("Finally shipment.load promise");
      });
    },

    importFile(shipment) {
      this.$http.post("shipment/import.jac", this.file, {
        headers : {
          "Content-Type" : "application/octet-stream"
        }
      }).then( response => {
        this.data = this.merge(response.body, shipment);
      }).catch(response => {
        console.log("Error uploading file", response);
        this.doProcessErrors(response.body);
      }).finally(() => {
        this.loading = false;
      });
    },

    isObject(item) {
      return (item && typeof item === 'object' && !Array.isArray(item));
    },

    customizer(objValue, srcValue, key, object, source) {
      // console.log("==================================", "key " + key);
      // console.log("typeof srcValue " + typeof srcValue, "typeof objValue " + typeof objValue);
      if (typeof objValue === "undefined" || objValue == null) {
        if (typeof srcValue === "undefined") {
          // console.log("return objValue (as srcValue is missing)");
          return objValue;
        }
        // console.log("return srcValue (as objValue is missing)");
        return srcValue;
      }
      if (typeof srcValue === "undefined") {
        // console.log("return objValue (as srcValue is missing)");
        return objValue;
      }
      if (typeof objValue === 'object') {
        if (srcValue == null) {
          // console.log("return objValue (as srcValue is null)");
          return objValue;
        }
        // console.log("merging objects");
        return _object.assignInWith(objValue, srcValue, this.customizer);
      }
      // console.log("using srcValue " + typeof srcValue)
      return srcValue;
    },

    merge(data, shipment) {
      let s = shipment;
      let tableKey = 0;
      s.id = null;
      s.manifest = "#";
      delete s.accepted;
      delete s.order_date;
      if (s.source && s.source.after)
        delete s.source.after;
      if (s.source && s.source.until)
        delete s.source.until;
      if (s.target && s.target.after)
        delete s.target.after;
      if (s.target && s.target.until)
        delete s.target.until;
      if (s.payment && s.payment.price)
        delete s.payment.price;

      this.loading = true;
      let r = data.filter(e => {
        delete e.order_date;
        if (typeof e  === "object" && !Object.keys(e).length){
          console.log("skipping empty shipment @" + tableKey);
          return false;
        }
        return true;
      }).map(e => {
        let //
        c = _lang.cloneDeep(s);
        c = _lang.cloneDeep(_object.assignInWith(c, e, { tableKey: tableKey++ }, this.customizer));
        return c;
      });
      this.loading = false;
      return r;
    },

    showMsg(text, type) {
      this.msg.text = (text) ? text : "";
      this.msg.type = (type) ? type : "info";
      this.msg.show = true;
    },

    findManifest() {
      if (this.loading)
        return;

      if (!this.isManifest) {
        this.showMsg( this.$t("Please select template manifest to import with"), "warning" );
        return;
      }

      this.loading = true;
      this.cleanup();
      let manifest = this.manifest.trim();
      shipments.findManifest({ length: 25, manifest: manifest }).then((result) => {
        let data = result.body.result;
        if (!data || data.length === 0) {
          let msgText = this.$t("Manifest not found");
          this.errors.push(msgText);
          this.showMsg(msgText, "error");
          this.loading = false;
          return;
        }
        if (data.length > 1) {
          this.showMsg(this.$t("More than one manifest found") + " - " + this.$t("Choose one from list"), "info");
        }
        else
        if (data.length === 1) {
          this.manifest = data[0].manifest;
        }

        console.log("data", data);

        this.addManifests( data );
        this.loading = false;

      }).catch((err) => {
        console.log("Error finding manifest", err);
        this.doProcessErrors(err.body);
        this.loading = false;
      }).finally(() => {
        console.log("Finally shipment.findManifest promise");
      });
    },

    addManifests(shipms) {
      for (let i=0; i < shipms.length; i++) {
        let shipm = shipms[i];

        if (this.manifests.find(elem => elem.manifest === shipm.manifest))
          continue;

        let hint = "";
        if (shipm.source && shipm.source.partner && shipm.source.partner.name)
          hint += (shipm.source.partner.name).substr(0, 15) + "…";
        if (shipm.source && shipm.source.address)
          hint += " " + (shipm.source.address).substr(0, 25) + "…";
        if (shipm.target && shipm.target.partner && shipm.target.partner.name)
          hint += " ─ " + (shipm.target.partner.name).substr(0, 15) + "…";
        if (shipm.target && shipm.target.address)
          hint += " " + (shipm.target.address).substr(0, 25) + "…";

        this.manifests.unshift({
          id: shipm.id,
          manifest: shipm.manifest,
          hint: hint,
        });
      };
      this.storePrefs();
    },
    removeManifest(item) {
      let index = this.manifests.findIndex(elem => elem.manifest === item.manifest);
      this.manifests.splice(index, 1);

      this.storePrefs();
    },
    storePrefs() {
      let prefs = null;
      try {
        prefs = JSON.parse(this.requestPrefs);
      } catch(e) {
        console.log("ERROR: Request preferences cannot be parsed!");
        prefs = {};
      }
      prefs.manifests = this.manifests;

      this.updateRequestPrefs( JSON.stringify(prefs) );
    },
    selectManifest(item) {
      this.manifest = item.manifest;
    },
    initPrefs() {
      let prefs = null;
      try {
        prefs = JSON.parse(this.requestPrefs);
      } catch(e) {
        console.log("ERROR: Request preferences cannot be parsed!");
        prefs = null;
      }

      if (prefs && prefs.manifests && prefs.manifests.length > 0) {
        this.manifests = JSON.parse( JSON.stringify(prefs.manifests) );
        let selected = this.manifests.find(elem => elem.isSelected);
        this.selectManifest( selected || this.manifests[0] );//this.manifests.length-1
      }
    },

    displayCOD (cod) {
      return showDualAmount(cod);
    }
  },

  mounted() {
    this.initPrefs();
  },

  watch: {
    manifest(val) {
      if (this.manifests.find(elem => elem.manifest === val)) {
        this.manifests.forEach(elem => {
          elem.isSelected = elem.manifest === val;
        });
        this.storePrefs();
      }
    }
  }
}
</script>

<style>
.line-spacing {
  line-height: 1.2;
}
/* make icon after text vertically aligned <vIcon/>Label */
.v-icon.material-icons {
  margin-bottom: -4px;
}
.mx-n2 {
  margin-left: -8px;
  margin-right: -8px;
  max-width: initial;
}
.mx-n3 {
  margin-left: -12px;
  margin-right: -12px;
  max-width: initial;
}
.mx-n4 {
  margin-left: -16px !important;
  margin-right: -16px !important;
  max-width: initial !important;
  width: auto;
}
.contents-item:nth-child(2n + 1) {
  background-color: #fff9;
}
</style>